import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  VStack,
  HStack,
  Box,
  Text,
  Heading,
  SimpleGrid,
  Badge,
  useToast,
  Spinner,
  Divider,
  Tabs,
  TabList,
  Tab,
  Alert,
  AlertIcon,
  Tooltip,
  Progress,
  Button,
  Input,
  InputGroup,
  InputLeftElement,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
} from '@chakra-ui/react';
import { 
  Trophy, 
  Calendar, 
  Users, 
  Clock, 
  DollarSign, 
  AlertCircle, 
  RefreshCw,
  Timer,
  Percent,
  Layers,
  ChevronDown,
  ChevronUp,
  Check,
  Zap,
  Ticket,
  Medal,
  Search,
  ChevronRight,
} from 'lucide-react';
import { POKER_COLORS } from '../theme/designSystem';
import PokerCard from '../components/ui/PokerCard';
import PokerButton from '../components/ui/PokerButton';
import TournamentListCard from '../components/tournament/TournamentListCard';
import TournamentDetailSheet from '../components/tournament/TournamentDetailSheet';
import { actionsFor, phaseOf, type ActionModel } from '../components/tournament/tournamentPresenter';
import { auth } from '../firebase';
import tournamentService from '../services/tournamentService';
import walletService from '../services/walletService';
import gameApiService from '../services/gameApiService';
import geoService from '../services/geoService'; //  Added for geo check

import { 
  Tournament, 
  TournamentStatus, 
  TournamentTier,
  TOURNAMENT_TIERS,
  TOURNAMENT_SCHEDULE,
  getStatusDisplay,
  getDayName,
  formatTime,
  calculatePrizePool,
  isRegistrationOpen,
} from '../types/tournamentTypes';

// ============================================================================
// COUNTDOWN HOOK - Updates every second
// ============================================================================

function useCountdown(targetDate: Date) {
  const [timeLeft, setTimeLeft] = useState(() => calculateTimeLeft(targetDate));

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft(targetDate));
    }, 1000);

    return () => clearInterval(timer);
  }, [targetDate]);

  return timeLeft;
}

function calculateTimeLeft(targetDate: Date) {
  const now = new Date();
  const diff = new Date(targetDate).getTime() - now.getTime();

  if (diff <= 0) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0, total: 0, formatted: 'Starting now', isExpired: true };
  }

  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((diff % (1000 * 60)) / 1000);

  let formatted = '';
  if (days > 0) {
    formatted = `${days}d ${hours}h ${minutes}m`;
  } else if (hours > 0) {
    formatted = `${hours}h ${minutes}m ${seconds}s`;
  } else if (minutes > 0) {
    formatted = `${minutes}m ${seconds}s`;
  } else {
    formatted = `${seconds}s`;
  }

  return { days, hours, minutes, seconds, total: diff, formatted, isExpired: false };
}

// ============================================================================
// MAIN TOURNAMENTS PAGE
// ============================================================================

// ============================================================================
// LiveResultsModal - live summary for a RUNNING (or finished) tournament
// ============================================================================
// Subscribes to the tournament doc and shows, updating in real time:
//   - LIVE/COMPLETED status, entrants, players left, prize pool
//   - The viewer's own finish (position + payout) once they bust
//   - Every paid position with the amount, and WHO claimed it so far -
//     positions still in play show as such. Busted players get the
//     "tournament review" they asked for without leaving the lobby.
// ============================================================================
const LiveResultsModal: React.FC<{
  tournamentId: string;
  currentUserId: string | null;
  onClose: () => void;
}> = ({ tournamentId, currentUserId, onClose }) => {
  const [t, setT] = useState<any | null>(null);

  useEffect(() => {
    let unsub: (() => void) | null = null;
    let cancelled = false;
    (async () => {
      const { doc, onSnapshot } = await import('firebase/firestore');
      const { db } = await import('../firebase');
      if (cancelled) return;
      unsub = onSnapshot(doc(db, 'tournaments', tournamentId),
        s => setT(s.exists() ? s.data() : null),
        () => setT(null));
    })();
    return () => { cancelled = true; if (unsub) unsub(); };
  }, [tournamentId]);

  const isLive = t && !['completed', 'cancelled'].includes(t.status);
  const entrants = (t?.registeredPlayers || []).length;
  const alive = (t?.activePlayers || []).length;
  const prizePool = t?.prizePool || 0;
  const isPC = t?.currency === 'playcoins';
  const money = (n: number) => isPC
    ? `${Math.round(n).toLocaleString()} PC`
    : `$${n.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
  const elims: any[] = t?.eliminations || [];
  const byPosition = new Map<number, any>();
  elims.forEach((e: any) => { if (e?.position) byPosition.set(e.position, e); });
  // The winner is never "eliminated" - finalResults (written at completion)
  // fills position 1 and any gaps.
  (t?.finalResults || []).forEach((e: any) => {
    if (e?.position && !byPosition.has(e.position)) byPosition.set(e.position, e);
  });
  const paidRows = (t?.payoutStructure || [])
    .slice()
    .sort((a: any, b: any) => (a.position || 0) - (b.position || 0))
    .map((p: any) => {
      const e = byPosition.get(p.position);
      const who = e ? (e.playerName || e.name || String(e.playerId || e.id || '').slice(0, 10)) : null;
      return {
        position: p.position,
        amount: Math.floor(prizePool * ((p.percentage || 0) / 100) * 100) / 100,
        who,
        isMe: !!currentUserId && !!e && (e.playerId === currentUserId || e.id === currentUserId),
      };
    });
  const myElim = currentUserId
    ? elims.find((e: any) => e?.playerId === currentUserId || e?.id === currentUserId)
    : null;
  const meAlive = !!currentUserId && (t?.activePlayers || []).includes(currentUserId);
  const myPayout = myElim?.position
    ? (paidRows.find((r: any) => r.position === myElim.position)?.amount || 0)
    : 0;
  const ordinal = (n: number) => {
    const s = ['th', 'st', 'nd', 'rd'], v = n % 100;
    return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 9400,
        background: 'rgba(0,0,0,0.7)', display: 'flex',
        alignItems: 'center', justifyContent: 'center', padding: 16,
      }}
      onClick={onClose}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: 'rgba(13, 31, 46, 0.98)', borderRadius: 16,
          border: '1px solid rgba(212, 175, 55, 0.5)',
          width: 'min(94vw, 520px)', maxHeight: '84vh', overflowY: 'auto',
          boxShadow: '0 18px 60px rgba(0,0,0,0.7)', color: '#e2e8f0',
          padding: '20px 22px',
        }}
      >
        {!t ? (
          <div style={{ color: '#94a3b8', fontSize: 14 }}>Loading tournament…</div>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 16, fontWeight: 800, color: '#fff' }}>{t.name || 'Tournament'}</div>
                <div style={{ marginTop: 4, display: 'flex', gap: 8, alignItems: 'center' }}>
                  <span style={{
                    fontSize: 10.5, fontWeight: 800, letterSpacing: '0.14em',
                    padding: '2px 10px', borderRadius: 10,
                    background: isLive ? 'rgba(34,197,94,0.16)' : 'rgba(148,163,184,0.16)',
                    color: isLive ? '#4ade80' : '#94a3b8',
                    border: `1px solid ${isLive ? 'rgba(74,222,128,0.5)' : 'rgba(148,163,184,0.4)'}`,
                  }}>
                    {isLive ? '● LIVE' : (t.status === 'cancelled' ? 'CANCELLED' : 'COMPLETED')}
                  </span>
                  <span style={{ color: '#94a3b8', fontSize: 12 }}>
                    {entrants} entrants · {isLive ? `${alive} still in` : 'finished'} · Prize pool {money(prizePool)}
                  </span>
                </div>
              </div>
              <button
                onClick={onClose}
                style={{ background: 'transparent', border: 'none', color: '#64748b', fontSize: 20, cursor: 'pointer', lineHeight: 1 }}
              >×</button>
            </div>

            {/* My result */}
            {(myElim || meAlive) && (
              <div style={{
                marginTop: 14, padding: '10px 14px', borderRadius: 10,
                background: meAlive ? 'rgba(34,197,94,0.1)' : 'rgba(212,175,55,0.1)',
                border: `1px solid ${meAlive ? 'rgba(74,222,128,0.4)' : 'rgba(212,175,55,0.4)'}`,
                fontSize: 13.5, fontWeight: 700,
                color: meAlive ? '#4ade80' : '#fde68a',
              }}>
                {meAlive
                  ? `You're still in - ${alive} of ${entrants} remaining`
                  : `You finished ${ordinal(myElim.position || 0)} of ${entrants}${myPayout > 0 ? ` - won ${money(myPayout)}` : ' - out of the money'}`}
              </div>
            )}

            {/* Payouts so far */}
            <div style={{ marginTop: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.16em', color: '#94a3b8' }}>
                PAYOUTS{isLive ? ' SO FAR' : ''}
              </div>
              <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
                {paidRows.length === 0 && (
                  <div style={{ color: '#64748b', fontSize: 13 }}>No payout structure on this tournament.</div>
                )}
                {paidRows.map((r: any) => (
                  <div
                    key={r.position}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '7px 12px', borderRadius: 8,
                      background: r.isMe ? 'rgba(212,175,55,0.12)' : 'rgba(255,255,255,0.03)',
                      border: r.isMe ? '1px solid rgba(212,175,55,0.45)' : '1px solid transparent',
                    }}
                  >
                    <span style={{ width: 38, color: '#d4af37', fontWeight: 800, fontSize: 13 }}>
                      {ordinal(r.position)}
                    </span>
                    <span style={{ flex: 1, fontSize: 13, color: r.who ? '#e2e8f0' : '#64748b', fontStyle: r.who ? 'normal' : 'italic' }}>
                      {r.who ? `${r.who}${r.isMe ? ' (you)' : ''}` : (isLive ? 'Still in play…' : 'Unclaimed')}
                    </span>
                    <span style={{ fontSize: 13, fontWeight: 700, color: '#4ade80' }}>{money(r.amount)}</span>
                  </div>
                ))}
              </div>
              {isLive && (
                <div style={{ marginTop: 10, color: '#64748b', fontSize: 11.5 }}>
                  Updates live as players bust. Positions without a name are still being played for.
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const TournamentsPage: React.FC = () => {
  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [loading, setLoading] = useState(true);
  const [registering, setRegistering] = useState<string | null>(null);
  const [rebuyingId, setRebuyingId] = useState<string | null>(null);
  const [liveResultsId, setLiveResultsId] = useState<string | null>(null);
  const catalogSeedTriedRef = useRef(false);
  const [userRegistrations, setUserRegistrations] = useState<Set<string>>(new Set());
  const [activeTab, setActiveTab] = useState(0);
  const [pkCashBalance, setPkCashBalance] = useState(0);
  const [playCoinsBalance, setPlayCoinsBalance] = useState(0); //  Added

  const navigate = useNavigate();
  // Tracks tournament IDs we've already auto-routed to so the subscription
  // doesn't keep re-navigating on every snapshot update.
  const alreadyRoutedRef = useRef<Set<string>>(new Set());
  // tournamentId -> tableId where the user is seated (post-start). Used by
  // the "Join my table" button on the tournament card as a manual fallback
  // when auto-route was missed (e.g. user was on another page).
  const [seatedTableMap, setSeatedTableMap] = useState<Record<string, string>>({});
  // Admin role check - controls visibility of the per-card "Force start"
  // button so test admins can kick off a tournament without first finding
  // and entering a table.
  const [isAdminUser, setIsAdminUser] = useState(false);
  const [isRestrictedState, setIsRestrictedState] = useState(false); //  Added

  const [isAdmin, setIsAdmin] = useState(false); //  Added for admin bypass

  const [viewingPlayCoins, setViewingPlayCoins] = useState(false); //  Admin toggle

  const toast = useToast();
  const currentUser = auth.currentUser;

  //  Check geo restriction AND admin status

  useEffect(() => {
    const checkGeo = async () => {
      if (currentUser) {
        try {
          const geoResult = await geoService.checkLocation();
          const adminBypassed = geoResult.adminBypassed === true;
          const restricted = geoResult.canPlayPKCash === false && !adminBypassed;
          
          setIsAdmin(adminBypassed);
          setIsRestrictedState(restricted);
          
          if (adminBypassed) {
            console.log('👑 [Tournaments] Admin bypass active - can view all tournaments');
          } else if (restricted) {
            console.log('📍 [Tournaments] Restricted state - showing PlayCoins tournaments');
          }
        } catch (error) {
          console.error('Geo check failed:', error);
        }
      }
    };
    checkGeo();
  }, [currentUser]);

  // Load balances (both PSS Coins and PlayCoins)
  useEffect(() => {
    const loadBalance = async () => {
      if (currentUser) {
        const [pkCash, playCoins] = await Promise.all([
          walletService.getBalance(currentUser.uid, 'pkcash'),
          walletService.getBalance(currentUser.uid, 'playcoins'),
        ]);
        setPkCashBalance(pkCash);
        setPlayCoinsBalance(playCoins);
      }
    };
    loadBalance();
  }, [currentUser]);

  // Subscribe to tournaments
  useEffect(() => {
    const unsubscribe = tournamentService.subscribeToUpcomingTournaments(async (data) => {
      setTournaments(data);
      setLoading(false);

      // NOTE: Weekly tournament generation runs server-side via the
      // generateWeeklyTournamentsScheduled Cloud Function. Admins can also
      // trigger it manually from AdminDashboard. The previous client-side
      // auto-generator silently failed for non-admins (Firestore rules
      // require admin to create tournament docs) and spammed the console;
      // it has been removed.

      // Update user registrations
      if (currentUser) {
        const registered = new Set<string>();
        data.forEach(t => {
          if (t.registeredPlayers.includes(currentUser.uid)) {
            registered.add(t.id);
          }
        });
        setUserRegistrations(registered);
      }
    });

    return () => unsubscribe();
  }, [currentUser, toast]);

  // ===== EMPTY-LOBBY SELF-HEAL (admin) =====
  // The daily catalog is cron-generated - and the local emulator runs NO
  // crons, so the schedule card promises events that never materialize.
  // When an admin lands on an EMPTY lobby, seed today's + tomorrow's
  // catalog automatically (idempotent server-side; once per session).
  useEffect(() => {
    if (loading || tournaments.length > 0 || !isAdminUser) return;
    if (catalogSeedTriedRef.current) return;
    try { if (sessionStorage.getItem('pss:catalogSeeded')) return; } catch { /* noop */ }
    catalogSeedTriedRef.current = true;
    try { sessionStorage.setItem('pss:catalogSeeded', '1'); } catch { /* noop */ }
    (async () => {
      const r: any = await gameApiService.generateDailyCatalog({ days: 2 });
      if (r.success) {
        toast({
          title: 'Tournament schedule seeded',
          description: `${r.totalCreated} event(s) created for today and tomorrow (the local emulator runs no scheduler).`,
          status: 'success',
          duration: 6000,
          isClosable: true,
        });
      } else {
        toast({
          title: 'Could not seed the schedule',
          description: r.error || 'generateDailyCatalog failed',
          status: 'error',
          duration: 6000,
          isClosable: true,
        });
      }
    })();
  }, [loading, tournaments, isAdminUser, toast]);

  // ===== AUTO-ROUTE TO SEATED TABLE WITH STARTING NOTIFICATION =====
  // The lobby subscription filters out tournaments with status 'running' so
  // it'll never see playerTableMap being populated. Subscribe to the RAW
  // doc for each registered tournament so we catch the start event whether
  // it happens 5 seconds or 5 hours after registration.
  useEffect(() => {
    if (!currentUser || userRegistrations.size === 0) return;
    let cancelled = false;
    const unsubs: Array<() => void> = [];

    (async () => {
      const { doc, onSnapshot } = await import('firebase/firestore');
      const { db } = await import('../firebase');
      if (cancelled) return;

      for (const tid of Array.from(userRegistrations)) {
        const unsub = onSnapshot(doc(db, 'tournaments', tid), (snap) => {
          if (!snap.exists() || cancelled) return;
          const data = snap.data();
          const map = data.playerTableMap as Record<string, string> | undefined;
          const myTableId = map?.[currentUser.uid];
          if (myTableId) {
            // Persist for the "Join my table" button fallback
            setSeatedTableMap(prev => prev[tid] === myTableId ? prev : { ...prev, [tid]: myTableId });
            // Respect explicit-leave: if the user clicked "Tournament lobby"
            // on the closed overlay (or otherwise opted out), don't yank
            // them back. They can use the "Join my table" button to re-enter.
            let userLeft = false;
            try {
              userLeft = sessionStorage.getItem(`pss:leftTournament:${tid}`) === '1';
            } catch {/* sessionStorage unavailable */}
            if (userLeft) return;
            if (!alreadyRoutedRef.current.has(tid)) {
              alreadyRoutedRef.current.add(tid);
              const tournamentName = data.name || 'Tournament';
              toast({
                title: 'Tournament starting',
                description: `${tournamentName} is dealing - taking you to your table now`,
                status: 'success',
                duration: 3000,
                position: 'top',
                isClosable: true,
              });
              // Brief pause so the toast registers before the route, then go.
              setTimeout(() => {
                console.log(`🎯 [Tournament] Auto-routing to table ${myTableId} for tournament ${tid}`);
                navigate(`/game/${myTableId}`);
              }, 1500);
            }
          }
        });
        unsubs.push(unsub);
      }
    })();

    return () => {
      cancelled = true;
      unsubs.forEach(u => u());
    };
  }, [currentUser, userRegistrations, navigate, toast]);

  // 🔗 Deep link from a push notification: /tournaments?register=<id>
  // lands the player ON that tournament - scrolled to it, highlighted,
  // Register button one tap away.
  const [highlightId, setHighlightId] = useState<string | null>(null);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const target = params.get('register');
    if (!target || tournaments.length === 0) return;
    setHighlightId(target);
    // Let the list paint, then bring the card into view
    setTimeout(() => {
      document.getElementById(`tournament-${target}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 400);
    const clear = setTimeout(() => setHighlightId(null), 8000);
    return () => clearTimeout(clear);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tournaments.length]);

  const handleRegister = async (tournament: Tournament) => {
    if (!currentUser) {
      toast({
        title: 'Not Logged In',
        description: 'Please log in to register',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    // Creator tournaments with code entry: ask for the code first.
    let inviteCode: string | undefined;
    if ((tournament as any).entry === 'code' && (tournament as any).hostId !== currentUser.uid) {
      const given = window.prompt(`${(tournament as any).hostName || 'The host'} shared a code for this tournament. Enter it to register:`);
      if (given === null) return;
      inviteCode = given.trim().toUpperCase();
      if (!inviteCode) return;
    }

    setRegistering(tournament.id);

    try {
      // Tournament service now handles wallet deduction internally
      const result = await tournamentService.registerPlayer(tournament.id, currentUser.uid, inviteCode);

      if (result.success) {
        // Refresh balance based on tournament currency
        const tierConfig = TOURNAMENT_TIERS[tournament.tier as keyof typeof TOURNAMENT_TIERS];
        const currency = tierConfig?.currency || 'pkcash';
        
        if (currency === 'playcoins') {
          const newBalance = await walletService.getBalance(currentUser.uid, 'playcoins');
          setPlayCoinsBalance(newBalance);
        } else {
          const newBalance = await walletService.getBalance(currentUser.uid, 'pkcash');
          setPkCashBalance(newBalance);
        }
        
        const amountStr = currency === 'playcoins' 
          ? `${tournament.buyInAmount.toLocaleString()} PlayCoins`
          : `${tournament.buyInAmount} PSS Coins`;
        
        toast({
          title: 'Registered! ',

          description: `You're registered for ${tournament.name}. ${amountStr} deducted.`,
          status: 'success',
          duration: 3000,
        });
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      console.error('Registration error:', error);
      toast({
        title: 'Registration Failed',
        description: error instanceof Error ? error.message : 'Could not register',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setRegistering(null);
    }
  };

  // Admin role check - drives Force Start visibility on tournament cards.
  useEffect(() => {
    if (!currentUser?.uid) {
      setIsAdminUser(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { doc, getDoc } = await import('firebase/firestore');
        const { db } = await import('../firebase');
        const snap = await getDoc(doc(db, 'users', currentUser.uid));
        if (cancelled) return;
        if (snap.exists()) {
          const d = snap.data();
          setIsAdminUser(d.isAdmin === true || d.role === 'admin');
        }
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [currentUser?.uid]);

  // Admin: force-start a tournament directly from the lobby card
  // (no need to first find a table and use the in-table panel).
  const handleForceStart = async (tournament: Tournament) => {
    if (!isAdminUser) return;
    if (!window.confirm(`Force-start "${tournament.name}" now? Creates tables and seats all registered players.`)) return;
    const r = await gameApiService.adminStartTournament(tournament.id);
    if (r.success) {
      toast({
        title: 'Tournament started',
        description: `Created ${r.tables?.length || 0} tables with ${r.playerCount || 0} players. Auto-routing in a moment...`,
        status: 'success',
        duration: 3500,
        position: 'top',
      });
    } else {
      toast({
        title: 'Start failed',
        description: r.error || 'Could not start tournament',
        status: 'error',
        duration: 4000,
      });
    }
  };

  // Click 'Spectate' on a running tournament - one-shot read of the
  // tournament doc, navigate to the first active table in spectate mode.
  // No registration required; the spectator endpoint adds the user to the
  // table's spectators array on arrival via GamePage's spectate effect.
  const handleSpectate = async (tournament: Tournament) => {
    try {
      const { doc, getDoc } = await import('firebase/firestore');
      const { db } = await import('../firebase');
      const snap = await getDoc(doc(db, 'tournaments', tournament.id));
      if (!snap.exists()) {
        toast({ title: 'Tournament not found', status: 'error', duration: 2500 });
        return;
      }
      const data = snap.data();
      const tables: string[] = data.activeTables || [];
      if (tables.length === 0) {
        toast({
          title: 'No active tables yet',
          description: 'Try again in a moment - the tournament is still seating players.',
          status: 'info',
          duration: 3000,
        });
        return;
      }
      // Land on the first table. From there the tournament sidebar (the
      // Tournament button in the game header) lets the user hop between
      // tables.
      navigate(`/game/${tables[0]}?spectate=1&tournamentId=${tournament.id}`);
    } catch (err) {
      console.error('Spectate error:', err);
      toast({ title: 'Could not open spectate', status: 'error', duration: 2500 });
    }
  };

  // Rebuy from the tournament card after busting. Looks up the user's
  // assigned table from the tournament's playerTableMap (or the existing
  // seatedTableMap cache) and hits /tournamentRebuy. On success, clears
  // the "left tournament" sessionStorage flag so the auto-route can put
  // the player back at the table, then navigates them straight there.
  const handleTournamentRebuy = async (tournament: Tournament) => {
    if (!currentUser) return;
    setRebuyingId(tournament.id);
    try {
      // Find the table to rebuy into. Prefer the persisted seatedTableMap;
      // fall back to refetching the tournament doc for the live mapping.
      let tableId = seatedTableMap[tournament.id];
      if (!tableId) {
        try {
          const { doc, getDoc } = await import('firebase/firestore');
          const { db } = await import('../firebase');
          const snap = await getDoc(doc(db, 'tournaments', tournament.id));
          const data = snap.exists() ? snap.data() : null;
          tableId = data?.playerTableMap?.[currentUser.uid];
        } catch {/* fall through */}
      }
      // No local table match is fine: the server resolves the player's
      // table itself (their old seat, or the emptiest active table) and
      // returns it - the client caches go stale the moment a bust removes
      // the seat, which is exactly when rebuys happen.
      const r = await gameApiService.tournamentRebuy(tournament.id, tableId || '');
      if (!r.success) {
        toast({
          title: 'Rebuy failed',
          description: r.error || 'Server rejected the rebuy.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        return;
      }
      try { sessionStorage.removeItem(`pss:leftTournament:${tournament.id}`); } catch {/* noop */}
      toast({
        title: 'Back in!',
        description: `Rebought for ${tournament.buyInAmount}. Routing you to the table.`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      navigate(`/game/${r.tableId || tableId}`);
    } catch (err: any) {
      toast({
        title: 'Rebuy failed',
        description: err?.message || 'Unknown error',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setRebuyingId(null);
    }
  };

  const handleUnregister = async (tournament: Tournament) => {
    if (!currentUser) return;

    setRegistering(tournament.id);

    try {
      // Tournament service now handles refund internally
      const result = await tournamentService.unregisterPlayer(tournament.id, currentUser.uid);

      if (result.success) {
        // Refresh balance based on tournament currency
        const tierConfig = TOURNAMENT_TIERS[tournament.tier as keyof typeof TOURNAMENT_TIERS];
        const currency = tierConfig?.currency || 'pkcash';
        
        if (currency === 'playcoins') {
          const newBalance = await walletService.getBalance(currentUser.uid, 'playcoins');
          setPlayCoinsBalance(newBalance);
        } else {
          const newBalance = await walletService.getBalance(currentUser.uid, 'pkcash');
          setPkCashBalance(newBalance);
        }
        
        toast({
          title: 'Unregistered',
          description: currency === 'playcoins'
            ? `${(result.refundAmount || tournament.buyInAmount).toLocaleString()} PlayCoins refunded to your wallet`
            : `${result.refundAmount || tournament.buyInAmount} PSS Coins has been refunded to your wallet`,
          status: 'info',
          duration: 3000,
        });
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      console.error('Unregister error:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Could not unregister',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setRegistering(null);
    }
  };

  // ===== RESULTS TAB: completed tournaments with winners + payouts =====
  // Public info - anyone can browse past tournaments and who cashed.
  const [completedTournaments, setCompletedTournaments] = useState<any[]>([]);
  const [loadingCompleted, setLoadingCompleted] = useState(false);
  useEffect(() => {
    if (activeTab !== 2) return;
    let cancelled = false;
    (async () => {
      setLoadingCompleted(true);
      try {
        const { collection, getDocs, query, where, orderBy, limit } = await import('firebase/firestore');
        const { db } = await import('../firebase');
        const q = query(
          collection(db, 'tournaments'),
          where('status', '==', 'completed'),
          orderBy('completedAt', 'desc'),
          limit(20)
        );
        const snap = await getDocs(q);
        if (!cancelled) setCompletedTournaments(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.error('Failed to load tournament results:', err);
      } finally {
        if (!cancelled) setLoadingCompleted(false);
      }
    })();
    return () => { cancelled = true; };
  }, [activeTab]);


  // ── Lobby view state ────────────────────────────────────────────────────
  // Tabs: 0 All, 1 My events, 2 Results, 3 Schedule. Filters sit on top of
  // the list rather than being tabs of their own.
  const [tierFilter, setTierFilter] = useState<'all' | 'low' | 'mid' | 'high'>('all');
  const [search, setSearch] = useState('');
  const [lateOnly, setLateOnly] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const [now, setNow] = useState(Date.now());
  // Tick once a second only while a countdown on screen shows seconds (a
  // start or late-reg close within ten minutes, or the detail sheet open);
  // otherwise every ten seconds, so a long list is not re-rendered 60
  // times a minute for nothing.
  const needsFastTick = !!openId || tournaments.some(t => {
    const start = t.startTime instanceof Date ? t.startTime.getTime() : new Date(t.startTime as any).getTime();
    const late = (t as any).lateRegistrationEndTime instanceof Date ? (t as any).lateRegistrationEndTime.getTime() : 0;
    const soon = (ms: number) => ms > now && ms - now < 10 * 60 * 1000;
    return soon(start) || soon(late);
  });
  useEffect(() => {
    const i = setInterval(() => setNow(Date.now()), needsFastTick ? 1000 : 10000);
    return () => clearInterval(i);
  }, [needsFastTick]);

  const showPlayCoins = isRestrictedState || viewingPlayCoins;
  const currencyOf = (t: Tournament) => {
    const tierConfig = TOURNAMENT_TIERS[t.tier as keyof typeof TOURNAMENT_TIERS];
    // The doc's currency is authoritative; the tier is a fallback for legacy docs.
    return t.currency || tierConfig?.currency || 'pkcash';
  };
  const inCurrency = tournaments.filter(t => (showPlayCoins ? currencyOf(t) === 'playcoins' : currencyOf(t) === 'pkcash'));
  const tierOf = (t: Tournament): 'low' | 'mid' | 'high' | 'other' =>
    t.tier === 'low' || t.tier === 'pc_low' ? 'low' : t.tier === 'mid' || t.tier === 'pc_mid' ? 'mid' : t.tier === 'high' || t.tier === 'pc_high' ? 'high' : 'other';
  const matchesFilters = (t: Tournament) => {
    if (tierFilter !== 'all' && tierOf(t) !== tierFilter) return false;
    if (lateOnly && phaseOf(t) !== 'late_reg') return false;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      const hay = `${t.name} ${t.description || ''} ${t.buyInAmount} ${(t as any).hostName || ''}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  };
  const visible = inCurrency.filter(matchesFilters).filter(t => (activeTab === 1 ? userRegistrations.has(t.id) : true));
  const mine = inCurrency.filter(t => userRegistrations.has(t.id));

  const startMsOf = (t: Tournament) => (t.startTime instanceof Date ? t.startTime.getTime() : new Date(t.startTime as any).getTime());
  const sectionOf = (t: Tournament): 'late' | 'soon' | 'live' | 'open' => {
    const p = phaseOf(t);
    if (p === 'late_reg') return 'late';
    if (p === 'live') return 'live';
    if (p === 'starting' || startMsOf(t) - now < 60 * 60 * 1000) return 'soon';
    return 'open';
  };
  const sections: Array<{ key: 'late' | 'soon' | 'open' | 'live'; title: string; icon: React.ReactNode; color: string; items: Tournament[] }> = [
    { key: 'late', title: 'Late registration available', icon: <Timer size={16} />, color: POKER_COLORS.gold, items: [] },
    { key: 'soon', title: 'Starting soon', icon: <Zap size={16} />, color: POKER_COLORS.cyan, items: [] },
    { key: 'open', title: 'Registering', icon: <Calendar size={16} />, color: '#93c5fd', items: [] },
    { key: 'live', title: 'Running now', icon: <Trophy size={16} />, color: '#4ade80', items: [] },
  ];
  visible.forEach(t => sections.find(s => s.key === sectionOf(t))!.items.push(t));
  sections.forEach(s => s.items.sort((a, b) => startMsOf(a) - startMsOf(b)));

  const balanceFor = (t: Tournament) => (currencyOf(t) === 'playcoins' ? playCoinsBalance : pkCashBalance);
  const actionsOf = (t: Tournament): ActionModel => actionsFor(t, {
    currentUserId: currentUser?.uid,
    isRegistered: userRegistrations.has(t.id),
    userBalance: balanceFor(t),
    myTableId: seatedTableMap[t.id],
    isAdminUser,
  }, now);
  const joinMyTable = (t: Tournament) => {
    const tid = seatedTableMap[t.id];
    if (tid) {
      try { sessionStorage.removeItem(`pss:leftTournament:${t.id}`); } catch { /* noop */ }
      navigate(`/game/${tid}`);
    }
  };
  const runPrimary = (t: Tournament, a: ActionModel) => {
    switch (a.primary.kind) {
      case 'register':
      case 'late_reg': return handleRegister(t);
      case 'join': return joinMyTable(t);
      case 'spectate': return handleSpectate(t);
      case 'rebuy': return handleTournamentRebuy(t);
      default: setOpenId(t.id);
    }
  };
  const runSecondary = (t: Tournament, kind: ActionModel['secondary'][number]['kind']) => {
    switch (kind) {
      case 'unregister': return handleUnregister(t);
      case 'spectate': return handleSpectate(t);
      case 'results': return setLiveResultsId(t.id);
      case 'force_start': return handleForceStart(t);
      case 'rebuy': return handleTournamentRebuy(t);
    }
  };
  const openTournament = openId ? tournaments.find(t => t.id === openId) || null : null;
  const busyFor = (t: Tournament) => registering === t.id || rebuyingId === t.id;

  const renderCard = (t: Tournament, compact = false) => (
    <TournamentListCard
      key={t.id}
      tournament={t}
      actions={actionsOf(t)}
      now={now}
      isRegistered={userRegistrations.has(t.id)}
      busy={busyFor(t)}
      highlighted={highlightId === t.id}
      compact={compact}
      onOpen={() => setOpenId(t.id)}
      onPrimary={() => runPrimary(t, actionsOf(t))}
    />
  );

  const iconTabs: Array<[number, string, React.ReactNode]> = [
    [0, 'All', <Trophy size={22} />],
    [1, `Mine${userRegistrations.size ? ` (${userRegistrations.size})` : ''}`, <Ticket size={22} />],
    [2, 'Results', <Medal size={22} />],
    [3, 'Schedule', <Calendar size={22} />],
  ];

  if (loading) {
    return (
      <Container maxW="3xl" py={8} px={{ base: 3, md: 6 }} pb={{ base: 24, md: 8 }}>
        <VStack spacing={8} align="center" justify="center" minH="50vh">
          <Spinner size="xl" color={POKER_COLORS.cyan} />
          <Text color="gray.400">Loading tournaments...</Text>
        </VStack>
      </Container>
    );
  }

  return (
    <Container maxW="3xl" py={{ base: 3, md: 6 }} px={{ base: 3, md: 6 }}
      css={{ paddingTop: 'max(env(safe-area-inset-top), 12px)', paddingBottom: 'max(env(safe-area-inset-bottom), 96px)' }}>
      <VStack spacing={4} align="stretch">
        {/* Header: title, balance, currency switch */}
        <HStack justify="space-between" align="flex-start" data-tutorial="tournaments-list">
          <Box>
            <Heading size="lg" color="white">Tournaments</Heading>
            <Text color="gray.400" fontSize="sm">{showPlayCoins ? 'PlayCoins · no rake' : 'PSS Coins'} · {inCurrency.length} event{inCurrency.length === 1 ? '' : 's'}</Text>
          </Box>
          <Box bg="#0f1722" border="1px solid rgba(148,163,184,0.18)" borderRadius="xl" px={3} py={2} textAlign="right">
            <Text color="gray.500" fontSize="xs">{showPlayCoins ? 'PlayCoins' : 'PSS Coins'}</Text>
            <Text color={showPlayCoins ? POKER_COLORS.cyan : POKER_COLORS.gold} fontWeight="900" fontSize="lg" lineHeight={1.1}>
              {showPlayCoins ? playCoinsBalance.toLocaleString() : `$${pkCashBalance.toFixed(2)}`}
            </Text>
          </Box>
        </HStack>

        {!isRestrictedState && (
          <HStack bg="#0f1722" borderRadius="full" p={1} border="1px solid rgba(148,163,184,0.18)" spacing={1}>
            {([['pss', 'PSS Coins'], ['pc', 'PlayCoins']] as Array<[string, string]>).map(([k, label]) => {
              const active = k === 'pc' ? viewingPlayCoins : !viewingPlayCoins;
              return (
                <Button key={k} flex={1} minH="42px" borderRadius="full" size="sm" fontSize="md" fontWeight="800"
                  bg={active ? (k === 'pc' ? POKER_COLORS.cyan : POKER_COLORS.gold) : 'transparent'} color={active ? '#0a1420' : 'gray.300'}
                  _hover={{ bg: active ? undefined : 'whiteAlpha.100' }} onClick={() => setViewingPlayCoins(k === 'pc')}>
                  {label}
                </Button>
              );
            })}
          </HStack>
        )}
        {isRestrictedState && !isAdmin && (
          <Text color="gray.400" fontSize="sm">PSS Coins tournaments are not available in your state. PlayCoins events are free to play with no rake.</Text>
        )}

        {/* Icon tabs */}
        <HStack spacing={0} borderBottom="1px solid rgba(148,163,184,0.18)">
          {iconTabs.map(([idx, label, icon]) => (
            <Box key={idx} as="button" flex={1} py={2} onClick={() => setActiveTab(idx)}
              color={activeTab === idx ? 'white' : 'gray.500'} borderBottom="3px solid" borderColor={activeTab === idx ? POKER_COLORS.cyan : 'transparent'}
              display="flex" flexDirection="column" alignItems="center" gap={1} transition="all 0.15s" _hover={{ color: 'white' }}>
              {icon}
              <Text fontSize="sm" fontWeight="700">{label}</Text>
            </Box>
          ))}
        </HStack>

        {(activeTab === 0 || activeTab === 1) && (
          <>
            {/* Filters */}
            <HStack spacing={2} overflowX="auto" sx={{ scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}>
              <Menu>
                <MenuButton as={Button} size="sm" minH="42px" borderRadius="xl" bg="#0f1722" border="1px solid rgba(148,163,184,0.18)" color="white" fontWeight="700"
                  rightIcon={<ChevronDown size={16} />} flexShrink={0} _hover={{ borderColor: POKER_COLORS.cyan }} _active={{ bg: '#0f1722' }}>
                  {tierFilter === 'all' ? 'Buy-in' : tierFilter === 'low' ? 'Low buy-in' : tierFilter === 'mid' ? 'Mid buy-in' : 'High buy-in'}
                </MenuButton>
                <MenuList bg="#0f1722" borderColor="rgba(148,163,184,0.25)">
                  {([['all', 'All buy-ins'], ['low', 'Low'], ['mid', 'Mid'], ['high', 'High']] as Array<[typeof tierFilter, string]>).map(([k, label]) => (
                    <MenuItem key={k} bg="transparent" _hover={{ bg: 'whiteAlpha.100' }} color={tierFilter === k ? POKER_COLORS.cyan : 'white'} onClick={() => setTierFilter(k)}>{label}</MenuItem>
                  ))}
                </MenuList>
              </Menu>
              <Button size="sm" minH="42px" borderRadius="xl" flexShrink={0} fontWeight="700" leftIcon={<Timer size={16} />}
                bg={lateOnly ? `${POKER_COLORS.gold}22` : '#0f1722'} color={lateOnly ? POKER_COLORS.gold : 'white'} border="1px solid" borderColor={lateOnly ? POKER_COLORS.gold : 'rgba(148,163,184,0.18)'}
                _hover={{ borderColor: POKER_COLORS.gold }} onClick={() => setLateOnly(v => !v)}>
                Late reg
              </Button>
              <Button size="sm" minH="42px" borderRadius="xl" flexShrink={0} fontWeight="700" leftIcon={<Ticket size={16} />}
                bg={activeTab === 1 ? `${POKER_COLORS.cyan}22` : '#0f1722'} color={activeTab === 1 ? POKER_COLORS.cyan : 'white'} border="1px solid" borderColor={activeTab === 1 ? POKER_COLORS.cyan : 'rgba(148,163,184,0.18)'}
                _hover={{ borderColor: POKER_COLORS.cyan }} onClick={() => setActiveTab(activeTab === 1 ? 0 : 1)}>
                My entries
              </Button>
            </HStack>
            <InputGroup>
              <InputLeftElement pointerEvents="none" h="48px"><Search size={18} color="#64748b" /></InputLeftElement>
              <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search tournaments" minH="48px" bg="#0f1722" border="1px solid rgba(148,163,184,0.18)" color="white" borderRadius="xl" fontSize="md" _focus={{ borderColor: POKER_COLORS.cyan }} />
            </InputGroup>

            {/* My tournaments carousel */}
            {activeTab === 0 && mine.length > 0 && !search && (
              <Box>
                <HStack justify="space-between" mb={2}>
                  <Text color="white" fontWeight="800" fontSize="lg">My tournaments</Text>
                  <Button size="sm" variant="link" color={POKER_COLORS.cyan} rightIcon={<ChevronRight size={16} />} onClick={() => setActiveTab(1)}>View all</Button>
                </HStack>
                <HStack spacing={3} overflowX="auto" pb={2} align="stretch" sx={{ scrollSnapType: 'x mandatory', scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' }, '& > *': { scrollSnapAlign: 'start' } }}>
                  {mine.map(t => renderCard(t, true))}
                </HStack>
              </Box>
            )}

            {/* Sections */}
            {visible.length === 0 ? (
              <Box bg="#0f1722" border="1px dashed rgba(148,163,184,0.3)" borderRadius="2xl" p={6} textAlign="center">
                <Calendar size={40} color={POKER_COLORS.cyan} style={{ margin: '0 auto 8px' }} />
                <Text color="gray.300" fontSize="md">
                  {activeTab === 1 ? 'You are not entered in any tournament yet.' : search || lateOnly || tierFilter !== 'all' ? 'Nothing matches those filters.' : 'No tournaments on the schedule right now.'}
                </Text>
                {activeTab === 1 && <Button mt={3} variant="link" color={POKER_COLORS.cyan} onClick={() => setActiveTab(0)}>Browse tournaments</Button>}
              </Box>
            ) : sections.filter(s => s.items.length > 0).map(s => (
              <Box key={s.key}>
                <HStack spacing={2} mb={2} px={1} color={s.color}>
                  {s.icon}
                  <Text fontWeight="800" fontSize="md" color={s.color}>{s.title}</Text>
                  <Text color="gray.500" fontSize="sm">· {s.items.length}</Text>
                </HStack>
                <VStack spacing={3} align="stretch">
                  {s.items.map(t => renderCard(t))}
                </VStack>
              </Box>
            ))}
          </>
        )}

        {/* Results */}
        {activeTab === 2 && (
          loadingCompleted ? (
            <PokerCard p={8}>
              <VStack spacing={4}>
                <Spinner color={POKER_COLORS.cyan} />
                <Text color="gray.400">Loading past tournaments...</Text>
              </VStack>
            </PokerCard>
          ) : completedTournaments.length === 0 ? (
            <PokerCard p={8}>
              <VStack spacing={3}>
                <Trophy size={40} color={POKER_COLORS.gold} />
                <Text color="gray.400" textAlign="center">
                  No completed tournaments yet. Results appear here as soon as a tournament crowns a winner.
                </Text>
              </VStack>
            </PokerCard>
          ) : (
            <VStack align="stretch" spacing={3}>
              {completedTournaments.map((t: any) => {
                const pool = t.prizePool || 0;
                const structure: any[] = Array.isArray(t.payoutStructure) ? t.payoutStructure : [];
                const standings: any[] = Array.isArray(t.finalResults) ? t.finalResults : [];
                const money = (n: number) => t.currency === 'playcoins' ? `${n.toLocaleString()} PC` : `$${n.toLocaleString()}`;
                const paidRows = structure.slice().sort((a: any, b: any) => (a.position || 0) - (b.position || 0)).slice(0, 5).map((s: any) => {
                  const fin = standings.find((f: any) => f?.position === s.position);
                  return { position: s.position, name: fin?.name || (fin?.userId ? String(fin.userId).substring(0, 8) : '—'), amount: Math.round(pool * ((s.percentage || 0) / 100) * 100) / 100 };
                });
                const completedDate = t.completedAt?.toDate ? t.completedAt.toDate().toLocaleString() : (t.completedAt ? new Date(t.completedAt).toLocaleString() : '');
                const entrants = Array.isArray(t.registeredPlayers) ? t.registeredPlayers.length : (t.currentPlayers || 0);
                return (
                  <Box key={t.id} bg="#0f1722" border="1px solid rgba(148,163,184,0.18)" borderRadius="2xl" p={4}>
                    <HStack align="flex-start" spacing={3}>
                      <Box w="48px" h="48px" borderRadius="xl" bg={`${POKER_COLORS.gold}1f`} display="flex" alignItems="center" justifyContent="center" flexShrink={0}><Trophy size={26} color={POKER_COLORS.gold} /></Box>
                      <Box flex={1} minW={0}>
                        <Text color="white" fontWeight="800" fontSize="md" noOfLines={2}>{t.name || t.id}</Text>
                        <Text color="gray.500" fontSize="xs">{completedDate}</Text>
                        <Text color="gray.400" fontSize="sm">{entrants} entrants · Prize pool {money(pool)}</Text>
                        {t.winnerName && <Text color={POKER_COLORS.gold} fontSize="sm" fontWeight="800">Champion: {t.winnerName}</Text>}
                      </Box>
                    </HStack>
                    {paidRows.length > 0 && (
                      <VStack align="stretch" spacing={1} mt={3}>
                        {paidRows.map(r => (
                          <HStack key={r.position} justify="space-between" fontSize="sm" bg="whiteAlpha.50" borderRadius="md" px={3} py={1.5}>
                            <Text color="gray.400" w="34px">#{r.position}</Text>
                            <Text color="white" flex={1} noOfLines={1}>{r.name}</Text>
                            <Text color={POKER_COLORS.gold} fontWeight="bold">{money(r.amount)}</Text>
                          </HStack>
                        ))}
                      </VStack>
                    )}
                  </Box>
                );
              })}
            </VStack>
          )
        )}

        {/* Schedule and rules */}
        {activeTab === 3 && (
          <VStack align="stretch" spacing={4}>
            <Box bg="#0f1722" border="1px solid rgba(148,163,184,0.18)" borderRadius="2xl" p={4}>
              <HStack mb={3}><Calendar size={20} color={POKER_COLORS.cyan} /><Text color="white" fontWeight="800" fontSize="lg">Weekly schedule</Text></HStack>
              <Text color="gray.400" fontSize="sm" mb={3}>PSS Coins events Mon, Wed and Fri. PlayCoins events Tue, Thu and Sat. All at 5:30 PM Pacific, registration open until the first deal, late entry until level 4.</Text>
              <SimpleGrid columns={{ base: 1, sm: 2, md: 3 }} gap={3}>
                {TOURNAMENT_SCHEDULE.map((schedule, idx) => (
                  <Box key={idx} bg="whiteAlpha.50" p={3} borderRadius="xl" border="1px solid" borderColor="whiteAlpha.100">
                    <Text color="white" fontWeight="800">{getDayName(schedule.dayOfWeek)}</Text>
                    <Text color={POKER_COLORS.cyan} fontSize="sm" fontWeight="700">{formatTime(schedule.hour, schedule.minute)} PT</Text>
                    <HStack mt={2} flexWrap="wrap" gap={1}>
                      {schedule.tiers.map(tier => (
                        <Badge key={tier} bg={`${TOURNAMENT_TIERS[tier].color}20`} color={TOURNAMENT_TIERS[tier].color} fontSize="xs" textTransform="none">
                          {TOURNAMENT_TIERS[tier].currency === 'playcoins' ? TOURNAMENT_TIERS[tier].buyIn.toLocaleString() : `$${TOURNAMENT_TIERS[tier].buyIn}`}
                        </Badge>
                      ))}
                    </HStack>
                  </Box>
                ))}
              </SimpleGrid>
            </Box>
            <Box bg="#0f1722" border="1px solid rgba(148,163,184,0.18)" borderRadius="2xl" p={4}>
              <HStack mb={3}><AlertCircle size={20} color={POKER_COLORS.cyan} /><Text color="white" fontWeight="800" fontSize="lg">How our tournaments run</Text></HStack>
              <VStack align="stretch" spacing={2}>
                {[
                  'Registration is open until the first hand is dealt; late entry and rebuys until the end of level 4',
                  'One add-on during the add-on window',
                  'PlayCoins tournaments are rake-free; PSS Coins tournaments take 10% of buy-ins',
                  'Five players to start; everyone is refunded if a tournament does not run',
                  '15 second decision timer with a 10 second time bank',
                  'Auto-fold when the timer runs out (auto-check when possible)',
                  'Tables balance automatically; hand-for-hand on the money bubble',
                  'No cap on entries',
                ].map((rule, idx) => (
                  <HStack key={idx} align="flex-start">
                    <Check size={16} color="#4ade80" style={{ marginTop: 3, flexShrink: 0 }} />
                    <Text color="gray.300" fontSize="md">{rule}</Text>
                  </HStack>
                ))}
              </VStack>
            </Box>
          </VStack>
        )}

        {/* Detail sheet */}
        <TournamentDetailSheet
          tournament={openTournament}
          actions={openTournament ? actionsOf(openTournament) : null}
          isOpen={!!openTournament}
          onClose={() => setOpenId(null)}
          now={now}
          busy={openTournament ? busyFor(openTournament) : false}
          currentUserId={currentUser?.uid}
          onPrimary={() => { if (openTournament) runPrimary(openTournament, actionsOf(openTournament)); }}
          onSecondary={(kind) => { if (openTournament) runSecondary(openTournament, kind); }}
        />

        {/* LIVE RESULTS - review a running tournament (finish, payouts so far) */}
        {liveResultsId && (
          <LiveResultsModal
            tournamentId={liveResultsId}
            currentUserId={currentUser?.uid || null}
            onClose={() => setLiveResultsId(null)}
          />
        )}
      </VStack>
    </Container>
  );
};

export default TournamentsPage;
